import { toast } from 'sonner';
import i18n from './i18n';

/**
 * Error codes that can be returned by the API
 */
export const ErrorCode = {
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  OFFLINE: 'OFFLINE',
  TIMEOUT: 'TIMEOUT',

  // Authentication errors
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  SESSION_EXPIRED: 'SESSION_EXPIRED',

  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  DUPLICATE_ENTRY: 'DUPLICATE_ENTRY',
  CONSTRAINT_VIOLATION: 'CONSTRAINT_VIOLATION',

  // Resource errors
  NOT_FOUND: 'NOT_FOUND',
  ALREADY_EXISTS: 'ALREADY_EXISTS',

  // Server errors
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  // Business logic errors
  OPERATION_FAILED: 'OPERATION_FAILED',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  RATE_LIMITED: 'RATE_LIMITED',

  // Unknown
  UNKNOWN: 'UNKNOWN',
} as const;

/**
 * Structured error interface
 */
export type ErrorCodeType = (typeof ErrorCode)[keyof typeof ErrorCode];

export interface AppError {
  code: ErrorCodeType;
  message: string;
  details?: unknown;
  statusCode?: number;
  isUserError?: boolean; // If true, the error is due to user action
  isRecoverable?: boolean; // If true, the action can be retried
  retryAfter?: number; // Seconds to wait before retry (for rate limiting)
}

/**
 * Error transformation utilities
 */
export class ErrorHandler {
  /**
   * Analyzes error message to determine the error code
   */
  private static analyzeErrorMessage(message: string): ErrorCodeType {
    // Type guard: ensure message is a string before calling toLowerCase
    if (typeof message !== 'string') {
      return ErrorCode.UNKNOWN;
    }

    const lowerMessage = message.toLowerCase();

    // Database constraint violations
    if (
      lowerMessage.includes('foreign key constraint') ||
      lowerMessage.includes('fk_')
    ) {
      return ErrorCode.CONSTRAINT_VIOLATION;
    }

    if (
      lowerMessage.includes('duplicate') ||
      lowerMessage.includes('already exists') ||
      lowerMessage.includes('unique constraint')
    ) {
      return ErrorCode.DUPLICATE_ENTRY;
    }

    // Network errors
    if (
      lowerMessage.includes('network') ||
      lowerMessage.includes('fetch failed') ||
      lowerMessage.includes('connection refused')
    ) {
      return ErrorCode.NETWORK_ERROR;
    }

    if (lowerMessage.includes('timeout')) {
      return ErrorCode.TIMEOUT;
    }

    // Auth errors
    if (lowerMessage.includes('unauthorized') || lowerMessage.includes('401')) {
      return ErrorCode.UNAUTHORIZED;
    }

    if (lowerMessage.includes('forbidden') || lowerMessage.includes('403')) {
      return ErrorCode.FORBIDDEN;
    }

    if (
      lowerMessage.includes('session expired') ||
      lowerMessage.includes('token expired')
    ) {
      return ErrorCode.SESSION_EXPIRED;
    }

    // Resource errors
    if (lowerMessage.includes('not found') || lowerMessage.includes('404')) {
      return ErrorCode.NOT_FOUND;
    }

    // Validation
    if (
      lowerMessage.includes('validation') ||
      lowerMessage.includes('invalid')
    ) {
      return ErrorCode.VALIDATION_ERROR;
    }

    // Rate limiting
    if (
      lowerMessage.includes('rate limit') ||
      lowerMessage.includes('too many requests') ||
      lowerMessage.includes('429')
    ) {
      return ErrorCode.RATE_LIMITED;
    }

    // Server errors
    if (
      lowerMessage.includes('internal server') ||
      lowerMessage.includes('500')
    ) {
      return ErrorCode.INTERNAL_SERVER_ERROR;
    }

    if (
      lowerMessage.includes('service unavailable') ||
      lowerMessage.includes('503')
    ) {
      return ErrorCode.SERVICE_UNAVAILABLE;
    }

    return ErrorCode.UNKNOWN;
  }

  /**
   * Transforms any error into a structured AppError
   */
  static transformError(error: unknown): AppError {
    // Type guard for AppError
    if (error && typeof error === 'object' && 'code' in error) {
      const maybeAppError = error as Record<string, unknown>;
      if (
        Object.values(ErrorCode).includes(maybeAppError.code as ErrorCodeType)
      ) {
        return error as AppError;
      }
    }

    // Check if offline
    if (!navigator.onLine) {
      return {
        code: ErrorCode.OFFLINE,
        message: 'errors.offline',
        isUserError: false,
        isRecoverable: true,
      };
    }

    // Extract information from different error formats
    let message = 'errors.unknown';
    let statusCode: number | undefined;
    let details: unknown;

    // Handle API response errors
    if (error && typeof error === 'object' && 'response' in error) {
      const errorWithResponse = error as {
        response: {
          status?: number;
          data?: { message?: unknown };
          statusText?: string;
        };
      };
      statusCode = errorWithResponse.response.status;

      // Type guard: ensure message is a string
      const dataMessage = errorWithResponse.response.data?.message;
      if (typeof dataMessage === 'string') {
        message = dataMessage;
      } else if (typeof errorWithResponse.response.statusText === 'string') {
        message = errorWithResponse.response.statusText;
      }
      // message already has a default value of 'errors.unknown'

      details = errorWithResponse.response.data;
    }
    // Handle Error objects
    else if (error instanceof Error) {
      message = error.message;
    }
    // Handle string errors
    else if (typeof error === 'string') {
      message = error;
    }
    // Handle objects with message property
    else if (error && typeof error === 'object' && 'message' in error) {
      const errorWithMessage = error as { message: unknown };
      // Type guard: ensure message is a string
      if (typeof errorWithMessage.message === 'string') {
        message = errorWithMessage.message;
      } else if (
        errorWithMessage.message !== null &&
        errorWithMessage.message !== undefined
      ) {
        // Convert non-string messages to string
        message = String(errorWithMessage.message);
      }
      details = error;
    }

    // Type guard: ensure message is a string before analyzing
    if (typeof message !== 'string') {
      message = 'errors.unknown';
    }

    // Analyze message to determine error code
    const code = this.analyzeErrorMessage(message);

    // Map status codes to error codes if not already determined
    let finalCode = code;
    if (code === ErrorCode.UNKNOWN && statusCode) {
      switch (statusCode) {
        case 401:
          finalCode = ErrorCode.UNAUTHORIZED;
          break;
        case 403:
          finalCode = ErrorCode.FORBIDDEN;
          break;
        case 404:
          finalCode = ErrorCode.NOT_FOUND;
          break;
        case 409:
          finalCode = ErrorCode.ALREADY_EXISTS;
          break;
        case 429:
          finalCode = ErrorCode.RATE_LIMITED;
          break;
        case 500:
          finalCode = ErrorCode.INTERNAL_SERVER_ERROR;
          break;
        case 503:
          finalCode = ErrorCode.SERVICE_UNAVAILABLE;
          break;
      }
    }

    // Determine if error is user-caused and recoverable
    const isUserError = (
      [
        ErrorCode.VALIDATION_ERROR,
        ErrorCode.DUPLICATE_ENTRY,
        ErrorCode.NOT_FOUND,
        ErrorCode.ALREADY_EXISTS,
        ErrorCode.INSUFFICIENT_PERMISSIONS,
      ] as ErrorCodeType[]
    ).includes(finalCode);

    const isRecoverable = (
      [
        ErrorCode.NETWORK_ERROR,
        ErrorCode.OFFLINE,
        ErrorCode.TIMEOUT,
        ErrorCode.SERVICE_UNAVAILABLE,
        ErrorCode.RATE_LIMITED,
      ] as ErrorCodeType[]
    ).includes(finalCode);

    return {
      code: finalCode,
      message,
      statusCode,
      details,
      isUserError,
      isRecoverable,
    };
  }

  /**
   * Gets user-friendly error message based on error code and context
   */
  static getUserMessage(error: AppError, context?: string): string {
    const namespace = context ? `${context}.errors` : 'common.errors';
    // Type guard: ensure error.code is a string before calling toLowerCase
    const codeString =
      typeof error.code === 'string'
        ? error.code
        : String(error.code || 'UNKNOWN');
    const key = `${namespace}.${codeString.toLowerCase()}`;

    // Try to get translated message
    let translatedMessage = i18n.exists(key)
      ? i18n.t(key)
      : i18n.t(`common.errors.${codeString.toLowerCase()}`);

    // If no translation found, use a fallback
    if (!translatedMessage || translatedMessage === key) {
      translatedMessage = this.getFallbackMessage(error.code);
    }

    return translatedMessage;
  }

  /**
   * Get fallback message when translation is not available
   */
  private static getFallbackMessage(code: ErrorCodeType): string {
    switch (code) {
      case ErrorCode.NETWORK_ERROR:
        return 'Connection error. Please check your internet connection.';
      case ErrorCode.OFFLINE:
        return 'You are offline. Please connect to the internet and try again.';
      case ErrorCode.TIMEOUT:
        return 'The request timed out. Please try again.';
      case ErrorCode.UNAUTHORIZED:
        return 'You need to log in to perform this action.';
      case ErrorCode.FORBIDDEN:
        return 'You do not have permission to perform this action.';
      case ErrorCode.SESSION_EXPIRED:
        return 'Your session has expired. Please log in again.';
      case ErrorCode.VALIDATION_ERROR:
        return 'Please check your input and try again.';
      case ErrorCode.DUPLICATE_ENTRY:
        return 'This item already exists.';
      case ErrorCode.CONSTRAINT_VIOLATION:
        return 'This action cannot be completed due to existing dependencies.';
      case ErrorCode.NOT_FOUND:
        return 'The requested item was not found.';
      case ErrorCode.ALREADY_EXISTS:
        return 'An item with the same information already exists.';
      case ErrorCode.INTERNAL_SERVER_ERROR:
        return 'Something went wrong on our end. Please try again later.';
      case ErrorCode.SERVICE_UNAVAILABLE:
        return 'The service is temporarily unavailable. Please try again later.';
      case ErrorCode.OPERATION_FAILED:
        return 'The operation could not be completed. Please try again.';
      case ErrorCode.INSUFFICIENT_PERMISSIONS:
        return 'You do not have sufficient permissions for this action.';
      case ErrorCode.RATE_LIMITED:
        return 'Too many requests. Please wait a moment and try again.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Shows error toast with appropriate styling based on error type
   */
  static showErrorToast(error: AppError, context?: string): void {
    const message = this.getUserMessage(error, context);

    // For recoverable errors, show with action button
    if (error.isRecoverable) {
      toast.error(message, {
        action: {
          label: i18n.t('common.retry'),
          onClick: () => window.location.reload(),
        },
        duration: 6000,
      });
    }
    // For user errors, show as warning
    else if (error.isUserError) {
      toast.warning(message, {
        duration: 5000,
      });
    }
    // For system errors, show as error
    else {
      toast.error(message, {
        duration: 5000,
      });
    }
  }

  /**
   * Logs error for debugging (in development mode)
   */
  static logError(error: AppError, context?: string): void {
    if (import.meta.env.DEV) {
      console.group(`🔴 Error: ${context || 'Unknown Context'}`);
      console.error('Code:', error.code);
      console.error('Message:', error.message);
      console.error('Status:', error.statusCode);
      console.error('Details:', error.details);
      console.error('User Error:', error.isUserError);
      console.error('Recoverable:', error.isRecoverable);
      console.groupEnd();
    }
  }

  /**
   * Handles error with logging and user notification
   */
  static handle(error: unknown, context?: string): AppError {
    const appError = this.transformError(error);
    this.logError(appError, context);
    this.showErrorToast(appError, context);
    return appError;
  }
}

/**
 * Hook-friendly error handler
 */
export const useErrorHandler = () => {
  const handleError = (error: unknown, context?: string) => {
    return ErrorHandler.handle(error, context);
  };

  const transformError = (error: unknown) => {
    return ErrorHandler.transformError(error);
  };

  const getUserMessage = (error: AppError, context?: string) => {
    return ErrorHandler.getUserMessage(error, context);
  };

  return {
    handleError,
    transformError,
    getUserMessage,
  };
};
